import type { Ref } from 'vue'
import type { GroundingRecoveryRequest } from '~~/shared/chat/groundingRecovery'
import type { WebChatResumeRequest, WebChatSendRequest } from '~~/shared/chat/chatTurnRequest'
import { activityStatusLabel, type ActivityStatusParams } from '~/utils/activityStatusLabel'
import type { WebChatMessage, WebChatMessageToolCall, MessageAttachment, ChatImage, ToolActivity } from '~/types/web-chat'
import type { JsonObject, QuickResearchActivityStep } from '~~/shared/toolCalls'
// Imported explicitly rather than left to Nuxt's auto-import injection (which `useWordChat.ts` also
// does for the same helpers). Every chat turn runs through here, so a module transform that silently
// skips injection surfaces as `ReferenceError: generateId is not defined` at send time — a runtime-only
// failure that typecheck can't catch, since the auto-import types resolve fine either way.
import {
  readNdjsonStream, generateId, windowMessages, type CitationData,
} from '~/composables/useStreamParser'
import type { ViewState } from '~~/shared/web-chat/viewState'
import { toWire, type ChatScope } from '~~/shared/web-chat/scope'
import { ToolKind } from '~~/shared/web-chat/tools'
import {
  clientToolKind,
  type ConfirmId,
} from '~/composables/web-chat/dispatch'
import { Tools, type ResumeTiming } from '~~/shared/web-chat/tools'
import { useDocumentsStore } from '~/stores/documents'
import { reportUsageLimit } from '~/composables/useUsageLimit'
import type { UsageLimitResponse } from '~~/shared/usage/usageLimit'

/** The plan a free-tier member's seat is on (`billing.plans`; MAR-1452, D19). */
const FREE_TIER_PLAN_ID = 'free_tier'
// Explicit: this file lives under composables/web-chat/, which Nuxt's composables auto-import does
// not scan (only the top level of app/composables) — same reasoning as `useRestoreScope`'s import in
// `PanelLayout.vue`.
import {
  appendSubagentStep,
  isRecord,
  mergeTopLevelToolCalls,
  normalizeJsonObject,
  normalizeValidationResult,
  normalizeWebChatToolCall,
  subagentStepLabel,
} from '~/composables/web-chat/normalizers'
import { isQueuedChatTurnResponse, streamQueuedChatJob } from '~/composables/web-chat/streamQueuedChatJob'
import { queuedChatCancelPath, queuedChatEmailPath, type QueuedChatEmailResponse } from '~~/shared/chat/queuedChatTurn'

export interface WebChatToolCall {
  name: string
  input: JsonObject
  /** ADK engine only: the function-call id to echo back as a clientToolResult to resume the session. */
  id?: string
}

/**
 * Whether a confirm tool resumes the paused session NOW or when the user decides.
 *
 * Declared on the tool in `shared/web-chat/tools.ts` (`resume`), not in a Set here: this was a
 * hand-kept list, i.e. a second place to remember when adding a confirm tool, and forgetting it
 * leaves the runner paused forever with no error.
 */
function resumesImmediately(name: string): boolean {
  return (Tools as Record<string, { resume?: ResumeTiming }>)[name]?.resume === 'immediate'
}

/**
 * Mutable streaming state shared between the store and this composable. Held in a single object so
 * `cancel()`, the conversation-CRUD reset paths, and the resume turn all read/write the same values.
 */
export interface StreamingState {
  abortController: AbortController | null
  /** Index of the assistant message currently being streamed — lets the ADK client-resume turn
   *  stream into the SAME message after the user's tool runs. */
  activeAssistantIndex: number
  /**
   * Whether the assistant message at `activeAssistantIndex` has a `conversation_messages` row yet.
   * Only that — its ID is `messages[activeAssistantIndex].id`, the same value the row is keyed by.
   * (This was `lastSavedAssistantDbId`, a shadow copy of a DB id the message didn't share; ids are now
   * minted once, client-side, and used verbatim as the primary key.)
   */
  assistantPersisted: boolean
}

export interface ChatStreamingDeps {
  messages: Ref<WebChatMessage[]>
  isStreaming: Ref<boolean>
  /** The running server tool's localized label, or '' (the grounding badge reads it). */
  toolStatus: Ref<string>
  /**
   * Whether the streaming turn is waiting for its answer (MAR-1057) — what the chat's live line shows.
   * On at send and at a resume, again after a tool call, a tool result or a `thinking` event; off at
   * the first piece of the answer (`delta`/`final`) and at the end of the turn. Display only, and never
   * on the message, so nothing persists it and a reload shows nothing (MAR-1020).
   */
  awaitingAnswer: Ref<boolean>
  /**
   * The streaming turn's server-tool activity (MAR-1057): one entry per tool the stream reported, set
   * HERE by the handler that hears about it — never by a watcher in the component (`apps/web/AGENTS.md`,
   * "Vue reactivity"). An entry is running from its `status` until its `tool_done`, the next status,
   * the first piece of the answer or the end of the turn. Reset when a stream starts.
   */
  toolActivities: Ref<ToolActivity[]>
  view: Ref<ViewState>
  contextDocumentIds: Ref<string[]>
  conversationId: Ref<string | null>
  /** Active case scope (null = global). Rides every /api/chat call so the backend can hard-filter
   *  the user-document tools to the case's linked documents. Single source of truth in the store. */
  activeScope: Ref<ChatScope>
  chatActive: Ref<boolean>
  modelId: Ref<string>
  /** SUPERADMIN-only QRA version toggle (`2.0` default / `3.4`). Rides every /api/chat call; the
   *  server re-gates on superadmin and only honors `3.4` for them. */
  qraVersion: Ref<string>
  state: StreamingState
  messageWindow: number
  t: (key: string, params?: Record<string, unknown>) => string
  authFetch: (url: string, opts: RequestInit) => Promise<Response>
  requestScroll: () => void
  createConversation: (firstMessage: string) => Promise<string>
  /** Persists under the message's OWN id; resolves to whether the row exists, not to an id. */
  saveMessage: (msg: WebChatMessage) => Promise<boolean>
  saveMessages: (msgs: WebChatMessage[]) => Promise<boolean | null>
  updateMessage: (messageId: string, content: string) => Promise<void>
  applyConfirmRequest: (msg: WebChatMessage, id: ConfirmId, input: Record<string, any>, clientToolId?: string) => void
  mergeCitationSnippets: (citations: Record<string, any> | undefined) => void
  /** Resolve the current messages' cite markers → popover snippets (DB). Runs when a turn finishes. */
  resolveCitations: () => void | Promise<void>
  /**
   * The client Firestore that holds `public_rag_jobs` (the plugin's `$ensureFirebaseDb`). Only a queued
   * Slow turn reads it (MAR-1390); absent, such a turn fails loudly instead of waiting forever.
   */
  ensureFirestore?: () => Promise<unknown>
}

export function useChatStreaming(deps: ChatStreamingDeps) {
  const {
    messages, isStreaming, toolStatus, awaitingAnswer, toolActivities, view, contextDocumentIds,
    conversationId, activeScope, chatActive, modelId, qraVersion, state, messageWindow, t, authFetch,
    requestScroll, createConversation, saveMessage, saveMessages, updateMessage,
    applyConfirmRequest, mergeCitationSnippets, resolveCitations,
  } = deps

  function ensureFirestoreOrThrow(): Promise<unknown> {
    if (!deps.ensureFirestore) return Promise.reject(new Error('A queued chat turn needs the client Firestore, and none was provided.'))
    return deps.ensureFirestore()
  }

  // ── The live line's state (MAR-1057) ──
  let activitySeq = 0

  /** Moves whatever tool is running to the finished chips. */
  function finishRunningTool(callId?: string) {
    for (const activity of toolActivities.value) {
      if (activity.completed) continue
      if (callId && activity.callId && activity.callId !== callId) continue
      activity.completed = true
    }
    if (!toolActivities.value.some(a => !a.completed)) toolStatus.value = ''
  }

  /** A stream starts (send or resume): no activity yet, and the turn waits for its answer. */
  function beginStream() {
    toolActivities.value = []
    awaitingAnswer.value = true
  }

  /** A stream ends, however it ended. */
  function endStream() {
    finishRunningTool()
    awaitingAnswer.value = false
  }

  function limitToolContextJson(value: string): string {
    const maxChars = 8000
    return value.length <= maxChars ? value : `${value.slice(0, maxChars)}...[truncated]`
  }

  function quickResearchContextForApi(output: Record<string, unknown>): JsonObject {
    const packet = isRecord(output.packet) ? output.packet : {}
    const context: Record<string, unknown> = {
      kind: 'run_query_legal_catalog_result',
      status: output.status || packet.status,
      results: output.results || packet.results || [],
      unresolved_gaps: output.unresolved_gaps || packet.unresolved_gaps || [],
    }
    const escalation = output.escalation || packet.escalation
    if (escalation) context.escalation = escalation
    return normalizeJsonObject(context)
  }

  function webChatToolContextForApi(message: WebChatMessage): string {
    if (message.role !== 'assistant' || !message.toolCalls?.length) return ''

    const contexts: string[] = []
    for (const toolCall of message.toolCalls) {
      if (toolCall.name !== 'run_query_legal_catalog') continue
      const output = toolCall.output
      if (!isRecord(output) || output.kind !== 'run_query_legal_catalog_result') {
        contexts.push('[Persisted tool context: this assistant message used run_query_legal_catalog.]')
        continue
      }
      const result = quickResearchContextForApi(output)
      contexts.push([
        '[Persisted tool context: this assistant message used run_query_legal_catalog.',
        'Quick research result JSON:',
        limitToolContextJson(JSON.stringify(result)),
        ']',
      ].join('\n'))
    }

    return contexts.join('\n\n')
  }

  function webChatMessageForApi(message: WebChatMessage): { role: WebChatMessage['role']; content: string } {
    const content = message.apiContent || message.content
    const toolContext = webChatToolContextForApi(message)
    return {
      role: message.role,
      content: toolContext ? `${content}\n\n${toolContext}` : content,
    }
  }

  // The readNdjsonStream handlers for one assistant turn. Extracted so the initial send AND the ADK
  // resume turn share one source (DRY). `topLevel` is a holder because onToolCalls reassigns it.
  // `cursor.msgIndex` is the message being written; the coordinator is the sole responder, so a turn
  // stays on one assistant message (`cursor.created` records it for the caller to persist).
  function buildStreamHandlers(ctx: {
    cursor: { msgIndex: number; created: number[] }
    toolCalls: WebChatToolCall[]
    topLevel: { value: WebChatMessageToolCall[] }
  }) {
    const { cursor, toolCalls, topLevel } = ctx
    return {
      // The gateway refused the organization mid-stream (MAR-1009): the red banner and its Activity
      // item. The turn's own inline sentence still arrives as its `final`.
      onUsageLimit(block: UsageLimitResponse) {
        reportUsageLimit(block)
        // The free plan's Fast (chat) allowance is spent: Slow, on its own Gemma allowance, is what is
        // left, so the message offers it (MAR-1390 round 3). Offered, never switched to.
        const msg = messages.value[cursor.msgIndex]
        if (msg && block.category === 'chat' && block.plan === FREE_TIER_PLAN_ID) msg.offerSlowTier = true
      },
      onThinking() {
        // Display only (MAR-1020): the turn is waiting, and nothing is written on the message.
        awaitingAnswer.value = true
        requestScroll()
      },
      onDelta(content: string) {
        // Live typing — replaced by the run's `final`. The answer has started, so the live line goes.
        finishRunningTool()
        awaitingAnswer.value = false
        messages.value[cursor.msgIndex]!.content += content
        requestScroll()
      },
      onFinal(content: string) {
        // ADK: the authoritative full text of the current run — replace whatever the display-only
        // deltas accumulated (a streamed draft can diverge from the model's final aggregate). Because
        // this replaces rather than appends, the answer can never stack.
        finishRunningTool()
        awaitingAnswer.value = false
        messages.value[cursor.msgIndex]!.content = content
        requestScroll()
      },
      onIterationStart() {
        const msg = messages.value[cursor.msgIndex]!
        if (!msg.segments) msg.segments = []
        msg.segments.push({ content: msg.content })
        msg.content = ''
        requestScroll()
      },
      onTool(name: string, input: Record<string, unknown>, id?: string) {
        // Route by the registry's kind. Confirm tools render a card; silent tools are queued and
        // executed by ChatPanel once the stream completes. `id` lets the browser resume the session.
        if (clientToolKind(name) === ToolKind.Confirm) {
          applyConfirmRequest(messages.value[cursor.msgIndex]!, name as ConfirmId, input, id)
          // Resume model for confirm tools (id-gated):
          //  • OFFER/escalation tools (present/download/workflows) — the agent's job was to PROPOSE, so
          //    resume the paused session with an ack now (queued like a silent tool, so ChatPanel's
          //    post-stream loop resumes it); the user's accept/decline then runs its own flow.
          //  • DESTRUCTIVE tools (delete/move/rename) are NOT queued here — they resume on the user's
          //    decision via resolveConfirmToolCall, so the agent learns the real outcome.
          if (id && resumesImmediately(name)) {
            toolCalls.push({ name, input: normalizeJsonObject(input), id })
          }
          requestScroll()
          return
        }
        toolCalls.push({ name, input: normalizeJsonObject(input), ...(id ? { id } : {}) })
        requestScroll()
      },
      onToolRejected(name: string, reason: string, id?: string) {
        // The server refused this call after its card had already been emitted (the `tool` event
        // rides the function CALL, which precedes execution). Retract the card so nothing rejected
        // is left on screen for the user to click, and let the model's own turn carry the reason —
        // it received the same rejection as its tool result (MAR-97).
        const msg = messages.value[cursor.msgIndex]
        if (!msg) return
        if (msg.card && (!id || msg.card.callId === id)) {
          delete msg.card
        }
        console.warn(`[webChatStore] server refused ${name}: ${reason}`)
        requestScroll()
      },
      onToolCalls(calls: unknown[]) {
        // The model called a tool: the turn goes on waiting, whatever text came before the call.
        awaitingAnswer.value = true
        topLevel.value = mergeTopLevelToolCalls(calls.map(normalizeWebChatToolCall), topLevel.value)
        messages.value[cursor.msgIndex]!.toolCalls = topLevel.value
        requestScroll()
      },
      onSubagentEvent(payload: { agent: string; toolCallId: string; event: unknown }) {
        const step = subagentStepLabel(t, payload.event)
        if (!step) return
        topLevel.value = appendSubagentStep(topLevel.value, payload.toolCallId, step)
        messages.value[cursor.msgIndex]!.toolCalls = topLevel.value
        requestScroll()
      },
      onValidationStart(payload: { validator: string }) {
        if (payload.validator !== 'answer_grounding') return
        // Retrieval is finished by the time grounding runs — clear the lingering activity status so its
        // chip (e.g. "Investigando…") is marked complete and the grounding badge's own "Verificando
        // fuentes…" spinner is the only live indicator (otherwise both spin at once).
        finishRunningTool()
        messages.value[cursor.msgIndex]!.validation = { status: 'running' }
        requestScroll()
      },
      onValidationResult(payload: { validator: string; result: unknown }) {
        if (payload.validator !== 'answer_grounding') return
        messages.value[cursor.msgIndex]!.validation = normalizeValidationResult(payload.result)
        requestScroll()
      },
      onStatus(message: string, code?: string, params?: Record<string, unknown>) {
        // "Evaluando…" follows a finished `quick_research`: the model is reasoning over the results,
        // which the live line already says. It starts nothing (MAR-1057).
        if (code === 'legal_evaluating') {
          finishRunningTool()
          awaitingAnswer.value = true
          requestScroll()
          return
        }
        const label = code
          ? activityStatusLabel(t, code, params as ActivityStatusParams, message)
          : message
        const callId = topLevel.value.length ? String(topLevel.value[topLevel.value.length - 1]!.id ?? '') || undefined : undefined
        // A status for the call that is already running is that call, not a new tool: the same call
        // can reach the browser twice (MAR-1057). It may only refresh the label.
        const sameCall = callId && toolActivities.value.find(a => !a.completed && a.callId === callId)
        if (sameCall) {
          if (label) { sameCall.label = label; toolStatus.value = label }
          return
        }
        // Otherwise a status names the server tool now running; the one before it has finished.
        finishRunningTool()
        if (!label) return
        const msg = messages.value[cursor.msgIndex]
        toolActivities.value.push({
          id: ++activitySeq,
          label,
          completed: false,
          segmentIndex: msg?.segments?.length ?? 0,
          startedAt: Date.now(),
          callId,
        })
        toolStatus.value = label
        awaitingAnswer.value = true
        requestScroll()
      },
      onToolDone(_name: string, id?: string) {
        // A server tool returned (MAR-1057): it joins the finished chips, and the model reasons on.
        finishRunningTool(id)
        awaitingAnswer.value = true
        requestScroll()
      },
      onToolResult(name: string, items: string[], citations?: Record<string, CitationData>, _docNames?: Record<string, string>, activity?: QuickResearchActivityStep[], result?: unknown) {
        if (name === 'create_user_directory') {
          try {
            useDocumentsStore().refreshAll()
          } catch (err) {
            console.warn('[webChatStore] Failed to auto-refresh document browser:', err)
          }
        }
        // A revision that applied changes the document list: a `copy` revision writes a NEW row the
        // browser has not fetched yet, and an `in_place` one changes the existing item's content. A
        // refusal (`ok: true, modifiedCount: 0`) changed nothing, so it refreshes nothing.
        if (name === 'revise_document' && isRecord(result)) {
          const outcome = result as { ok?: unknown, modifiedCount?: unknown }
          if (outcome.ok === true && typeof outcome.modifiedCount === 'number' && outcome.modifiedCount > 0) {
            try {
              useDocumentsStore().refreshAll()
            } catch (err) {
              console.warn('[webChatStore] Failed to auto-refresh document browser:', err)
            }
          }
        }
        // The server only attaches `citations` to a tool_result for citation-emitting legal tools
        // (`LEGAL_TOOLS` in toolResultSummary.ts), so its presence is the signal — no tool-name
        // allowlist to keep in sync. mergeCitationSnippets no-ops on an empty map.
        if (citations) {
          mergeCitationSnippets(citations)
          requestScroll()
        }
      },
    }
  }

  async function sendMessage(content: string, attachments?: MessageAttachment[], options?: { displayContent?: string; hiddenFromUi?: boolean; persist?: boolean | 'answer'; images?: ChatImage[]; recovery?: GroundingRecoveryRequest }): Promise<{ message: WebChatMessage | undefined; toolCalls: WebChatToolCall[] }> {
    // Switch to the chat view immediately, before the createConversation() round-trip and
    // the first message push, so the welcome screen doesn't flash after the user sends.
    chatActive.value = true
    // `'answer'` (MAR-206): save the ANSWER but not the user turn that asked for it. A grounding
    // recovery's user turn is a hidden sentinel the user never wrote; saved, it would reappear in the
    // history as a message, and an edit could branch from it. It still needs a conversation to join.
    const persistUserTurn = options?.persist !== 'answer'
    const shouldPersist = options?.persist !== false && (persistUserTurn || !!conversationId.value)
    if (shouldPersist && !conversationId.value) {
      await createConversation(options?.displayContent || content)
    }

    const userMsg: WebChatMessage = {
      id: generateId(),
      role: 'user',
      content: options?.displayContent || content,
      apiContent: options?.displayContent ? content : undefined,
      hiddenFromUi: options?.hiddenFromUi,
      attachments,
      images: options?.images?.length ? options.images : undefined,
      timestamp: Date.now()
    }
    messages.value.push(userMsg)
    requestScroll()

    const assistantMsg: WebChatMessage = { id: generateId(), role: 'assistant', content: '', timestamp: Math.max(Date.now(), userMsg.timestamp + 1) }
    messages.value.push(assistantMsg)
    requestScroll()
    const msgIndex = messages.value.length - 1
    state.activeAssistantIndex = msgIndex
    // Write target for the stream. The coordinator is the sole responder, so a turn stays on this one
    // assistant message; `created` records extra bubbles the finally block persists (currently none).
    const cursor = { msgIndex, created: [] as number[] }

    isStreaming.value = true
    beginStream()
    state.assistantPersisted = false
    const toolCalls: WebChatToolCall[] = []
    const topLevel = { value: [] as WebChatMessageToolCall[] }

    state.abortController = new AbortController()
    const { signal } = state.abortController

    try {
      if (shouldPersist) {
        // Both rows are keyed by the ids these messages already carry, so there is nothing to read
        // back — only whether the write happened.
        if (!(await saveMessages(persistUserTurn ? [userMsg, assistantMsg] : [assistantMsg]))) {
          throw new Error('Failed to persist initial chat messages')
        }
        state.assistantPersisted = true
      }

      const history = messages.value
        .filter(m => m.content.length > 0 || m.id === assistantMsg.id)
        .slice(0, -1)
        .map(webChatMessageForApi)

      const response = await authFetch('/api/chat', {
        method: 'POST',
        body: JSON.stringify({
          model: modelId.value,
          qraVersion: qraVersion.value,
          messages: windowMessages(history, messageWindow),
          webChatMode: true,
          promptKey: 'web-chat',
          view: view.value,
          contextDocumentIds: contextDocumentIds.value,
          // The conversation id is the ADK SESSION key, not just a persistence target — so a hidden,
          // non-persisted turn (workflow follow-up, claim revision) has to send it too, or the server
          // mints a random session id and the turn runs with an empty history and leaks an
          // `agent_sessions` row nothing owns (MAR-94). Only the message ids below are gated on the
          // rows existing; the server authorizes a bare conversation id on its own.
          conversationId: state.assistantPersisted || !shouldPersist ? conversationId.value : undefined,
          ...toWire(activeScope.value),
          // Both ids are the rows' primary keys. `userMessageId` rides the ADK user event so a later
          // edit can point at it; `assistantMessageId` is the parent for persisted server tool_calls.
          // Sent only once the rows exist — the server 404s otherwise.
          ...(state.assistantPersisted ? { assistantMessageId: assistantMsg.id, ...(persistUserTurn ? { userMessageId: userMsg.id } : {}) } : {}),
          // Vision: images ride the fresh turn as a top-level field (the server attaches them to the
          // user turn's genai parts). History stays text-only.
          images: userMsg.images?.map(i => ({ mimeType: i.mimeType, data: i.data })),
          // Answer-grounding recovery (MAR-206): what to recover, never how — the coordinator words
          // it. See `shared/chat/groundingRecovery.ts`.
          ...(options?.recovery ? { recovery: options.recovery } : {}),
        } satisfies WebChatSendRequest),
        headers: { 'Content-Type': 'application/json' },
        signal,
      })

      const handlers = buildStreamHandlers({ cursor, toolCalls, topLevel })
      // A Slow (Gemma) turn is QUEUED, not streamed (MAR-1390): the server answers `{ queued, jobId }`
      // and the turn's events arrive on its Firestore job doc instead — through the same handlers.
      const queued = response.headers.get('content-type')?.includes('application/json')
        ? await response.json().catch(() => null)
        : null
      if (isQueuedChatTurnResponse(queued)) {
        await streamQueuedChatJob(queued.jobId, handlers, {
          signal,
          ensureFirestore: ensureFirestoreOrThrow,
          // Not on `signal`: it is the abort this call reports.
          cancelJob: jobId => authFetch(queuedChatCancelPath(jobId), { method: 'POST' }),
          // The bubble's stage and place in line (MAR-1424), carried over the in-flight email request.
          onProgress: (progress) => {
            const target = messages.value[cursor.msgIndex]
            if (!target) return
            const prior = target.queuedTurn
            target.queuedTurn = {
              ...progress,
              emailRequested: progress.emailRequested || !!prior?.emailRequested,
              email: progress.email ?? prior?.email ?? null,
              emailPending: prior?.emailPending,
            }
          },
        })
      } else {
        const reader = queued ? undefined : response.body?.getReader()
        if (reader) await readNdjsonStream(reader, handlers, signal)
      }
      // Turn done — resolve the answer's cite markers to popover snippets (DB). Best-effort.
      await Promise.resolve(resolveCitations()).catch(() => {})
    } catch (err) {
      if (!signal.aborted) console.error('Companion chat error:', err)
      // The message may have been truncated away — see the finally block. An edit/regenerate splices
      // the thread AND aborts the stream, so by the time we land here `cursor.msgIndex` can point past
      // the end. Both branches below used to assume it resolved; only the error one checked, so a
      // SECOND edit while the first was still streaming threw here and took the whole send with it.
      const target = messages.value[cursor.msgIndex]
      if (target) {
        if (signal.aborted) target.content += target.content ? '\n\n(Cancelado)' : '(Cancelado)'
        else target.content = 'Lo siento, ocurrió un error. Por favor intenta de nuevo.'
        requestScroll()
      }
    } finally {
      state.abortController = null
      isStreaming.value = false
      endStream()
      // A queued turn's progress card goes with the turn (MAR-1424 AC4).
      const finished = messages.value[cursor.msgIndex]
      if (finished?.queuedTurn) delete finished.queuedTurn

      // The turn's messages can be GONE by now: `truncateFrom` (edit/regenerate) splices the thread
      // and aborts mid-stream, so every index captured before the abort is stale. Nothing to persist
      // in that case — the user branched away from this turn.
      const anchor = messages.value[msgIndex]
      if (state.assistantPersisted && anchor) {
        // Anchor message (saved up front) — persist its final content, under its own id.
        if (anchor.content) await updateMessage(anchor.id, anchor.content)
        // Boundary-created messages (ADK coordinator+specialist split) — each as its own row, in order.
        for (const idx of cursor.created) {
          const created = messages.value[idx]
          if (created?.content && shouldPersist) await saveMessage(created)
        }
      }
    }

    // `message` is undefined when the turn was truncated away mid-stream (edit/regenerate).
    return { message: messages.value[cursor.msgIndex], toolCalls }
  }

  /**
   * After the browser executes a paused client tool, deliver the result(s) back to resume the
   * suspended ADK session and stream the continuation into the SAME assistant message. Returns any
   * further client tools the resumed turn emitted, so the caller can loop.
   */
  async function resumeWithClientToolResults(
    results: Array<{ id: string; name: string; response: unknown }>,
    opts?: { anchorIndex?: number },
  ): Promise<WebChatToolCall[]> {
    if (results.length === 0 || isStreaming.value) return []
    // `anchorIndex` is the message whose card is being answered. When it is not the turn this
    // session is tracking, the card was rebuilt from the thread's history (MAR-991): that message
    // came off the server, so it has a row, and the continuation streams into it.
    const anchor = opts?.anchorIndex
    if (anchor !== undefined && anchor >= 0 && anchor !== state.activeAssistantIndex && messages.value[anchor]) {
      state.activeAssistantIndex = anchor
      state.assistantPersisted = !!conversationId.value
    }
    if (state.activeAssistantIndex < 0) return []
    const msgIndex = state.activeAssistantIndex
    const cursor = { msgIndex, created: [] as number[] }

    isStreaming.value = true
    beginStream()
    state.abortController = new AbortController()
    const { signal } = state.abortController
    const toolCalls: WebChatToolCall[] = []
    const topLevel = { value: messages.value[msgIndex]?.toolCalls ?? [] }

    try {
      const history = messages.value
        .filter(m => m.content.length > 0)
        .map(webChatMessageForApi)

      const response = await authFetch('/api/chat', {
        method: 'POST',
        body: JSON.stringify({
          model: modelId.value,
          qraVersion: qraVersion.value,
          messages: windowMessages(history, messageWindow),
          webChatMode: true,
          promptKey: 'web-chat',
          view: view.value,
          contextDocumentIds: contextDocumentIds.value,
          conversationId: conversationId.value || undefined,
          ...toWire(activeScope.value),
          // No `userMessageId`: a resume turn delivers a functionResponse, not a message.
          ...(state.assistantPersisted ? { assistantMessageId: messages.value[msgIndex]?.id } : {}),
          clientToolResults: results,
        } satisfies WebChatResumeRequest),
        headers: { 'Content-Type': 'application/json' },
        signal,
      })

      const reader = response.body?.getReader()
      if (reader) {
        await readNdjsonStream(reader, buildStreamHandlers({ cursor, toolCalls, topLevel }), signal)
      }
      // Turn done — resolve the answer's cite markers to popover snippets (DB). Best-effort.
      await Promise.resolve(resolveCitations()).catch(() => {})
    } catch (err) {
      if (!signal.aborted) console.error('Companion resume error:', err)
    } finally {
      state.abortController = null
      isStreaming.value = false
      endStream()
      // Anchor message (the one we resumed into) — persist its final content, under its own id.
      const anchor = messages.value[msgIndex]
      if (state.assistantPersisted && anchor?.content) await updateMessage(anchor.id, anchor.content)
      // Any message split off mid-resume (ADK coordinator+specialist) — persist as its own row.
      for (const idx of cursor.created) {
        const created = messages.value[idx]
        if (created?.content) await saveMessage(created)
      }
    }

    return toolCalls
  }

  /**
   * "Envíamelo por correo" (MAR-1424): ask the app to mark this queued turn's job, so the worker
   * emails the answer when it finishes. Only while the turn is in progress — `queuedTurn` is dropped
   * when it ends, and the server refuses a finished job anyway. Best-effort: a failure leaves the
   * button where it was.
   */
  async function requestQueuedTurnEmail(messageId: string): Promise<void> {
    const queued = messages.value.find(m => m.id === messageId)?.queuedTurn
    if (!queued || queued.emailRequested || queued.emailPending) return
    queued.emailPending = true
    try {
      const response = await authFetch(queuedChatEmailPath(queued.jobId), { method: 'POST' })
      const body = response.ok ? await response.json().catch(() => null) as QueuedChatEmailResponse | null : null
      // Re-read: a snapshot may have replaced the object while the request was out.
      const current = messages.value.find(m => m.id === messageId)?.queuedTurn
      if (body?.requested && current) {
        current.emailRequested = true
        current.email = body.email ?? current.email
      }
    } catch (err) {
      console.warn('[webChat] could not request the queued turn by email:', err)
    } finally {
      const current = messages.value.find(m => m.id === messageId)?.queuedTurn
      if (current) current.emailPending = false
    }
  }

  return {
    buildStreamHandlers,
    sendMessage,
    requestQueuedTurnEmail,
    resumeWithClientToolResults,
    webChatMessageForApi,
    webChatToolContextForApi,
    quickResearchContextForApi,
    limitToolContextJson,
  }
}
